import type { AnyObject } from "@entrilia/shared/types.ts";
import type { SxProps, Theme } from "@mui/material";
import { alpha, Box, List, Tooltip } from "@mui/material";
import type { JSX, MouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { DropTargetMonitor } from "react-dnd";
import { useDrop } from "react-dnd";
import { CalculationType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { DraggableFieldType, DraggableMetaType, FieldWrapper } from "../../../../../../../../api/types/svTypes.ts";
import { newCustomColumnField } from "../../../../utilities/fieldsHelper.ts";
import { insertItemAt } from "../../../../utilities/Utilities.tsx";
import AddField from "../../../viewer/common/fields/area-fields/AddField.tsx";
import { AreaFieldItemContainer } from "../../../viewer/common/fields/area-fields/AreaFieldItemContainer.tsx";
import type { FieldItemProps } from "../../../viewer/common/fields/area-fields/areaFiledItem.types.ts";
import type { ShowFieldOptionsSettings } from "../../../viewer/common/Types.ts";
import type { HoveredFieldItemIndex } from "../../utils/dragDropHelper.ts";
import { getInsertToIndex, hoveredIndexIsDefined } from "../../utils/dragDropHelper.ts";
import useDragLeave from "@entrilia/shared/hooks/useDragLeave";
import { extractAreaItemType, extractMeta, isFieldWrapper, wrapItem } from "../utilities/dropFieldContainerHelper.ts";
import AddFieldMenu from "./AddFieldMenu.tsx";
import type { CustomColumnData } from "./custom-field/customColumn.types.ts";
import CustomColumnConditionValueSetup from "./custom-field/CustomColumnConditionValueSetup.tsx";
import CustomFormulaDialog from "./custom-field/CustomFormulaDialog.tsx";
import NewCustomColumnCalculationSetup from "./custom-field/NewCustomColumnCalculationSetup.tsx";
import DropFieldPlaceholder from "./DropFieldPlaceholder.tsx";
import FieldContainerHeading from "./FieldContainerHeading.tsx";
import FieldDestinationHeader from "./FieldDestinationHeader.tsx";
import FieldOptionPopupContainer from "./measures/FieldOptionPopupContainer.tsx";
import { useFieldDestination } from "./useFieldDestination.ts";

const HOVER_DELAY_MS = 1000;

export interface AddFieldAction {
  label: string;
  onClick: (anchorEl: HTMLElement) => void;
}

interface Props<T extends DraggableFieldType, TMeta extends DraggableMetaType> {
  title: string;
  headingActions?: ReactNode;
  containerSx?: SxProps<Theme>;
  areaFieldType: string;
  fields: T[];
  maxFields?: number;
  acceptedDropTypes: string[];
  readonly?: boolean;
  canAddCustomField?: boolean;
  addFieldAction?: AddFieldAction;
  hideAddField?: boolean;
  renderListItem?: (props: FieldItemProps<T>) => JSX.Element;
  canDropItem?: (item: FieldWrapper<T> | TMeta) => boolean;
  getKeyValue: (field: FieldWrapper<T> | TMeta) => string;
  formatCaption?: (field: T) => JSX.Element | undefined;
  onAddItem: (item: T, index: number) => void;
  onMoveItem: (item: T, newIndex: number) => void;
  onRemoveItem: (item: T) => void;
  showOptions?: (settings: ShowFieldOptionsSettings<T>) => void;
  createListItem: (meta: TMeta) => T | undefined;
}
export const DropFieldContainer = <T extends DraggableFieldType, TMeta extends DraggableMetaType>(
  props: Props<T, TMeta>
) => {
  const {
    title,
    headingActions,
    areaFieldType,
    fields,
    maxFields,
    acceptedDropTypes,
    readonly,
    canAddCustomField,
    addFieldAction,
    hideAddField,
    containerSx,
    renderListItem,
    formatCaption,
    canDropItem,
    getKeyValue,
    onAddItem,
    onMoveItem,
    onRemoveItem,
    showOptions,
    createListItem,
  } = props;
  const { store: destinationStore, destination } = useFieldDestination();
  const destinationId = useId();
  const [freeSpaceHovered, setFreeSpaceHovered] = useState(false);
  const [freeSpaceHighlighted, setFreeSpaceHighlighted] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(hoverTimeoutRef.current), []);

  const canSelectDestination = !readonly && (maxFields === undefined || fields.length < maxFields);

  const fieldDestination = useMemo(
    () => ({
      id: destinationId,
      title,
      canAdd: (meta: DraggableMetaType) =>
        canSelectDestination && acceptedDropTypes.includes(meta.itemType) && (canDropItem?.(meta as TMeta) ?? true),
      add: (meta: DraggableMetaType) => {
        const field = createListItem(meta as TMeta);
        if (field) {
          onAddItem(field, fields.length);
        }
      },
    }),
    [destinationId, title, canSelectDestination, acceptedDropTypes, canDropItem, createListItem, onAddItem, fields]
  );

  useEffect(() => {
    if (canSelectDestination) {
      destinationStore?.register(fieldDestination);
    } else {
      destinationStore?.unregister(destinationId);
    }
  }, [destinationStore, destinationId, fieldDestination, canSelectDestination]);

  useEffect(() => () => destinationStore?.unregister(destinationId), [destinationStore, destinationId]);

  const [addFieldMenuItemAnchorEl, setAddFieldMenuItemAnchorEl] = useState<HTMLElement | null>(null);
  const [showAddFieldMenu, setShowAddFieldMenu] = useState(false);
  const [selectedCalculationType, setSelectedCalculationType] = useState<CalculationType | null>(null);

  const wrappedFields = useMemo(
    () => fields.map((field, index) => wrapItem(field, index, areaFieldType)),
    [fields, areaFieldType]
  );
  const [dragPreview, setDragPreview] = useState<{ source: FieldWrapper<T>[]; items: FieldWrapper<T>[] }>();
  const areaFields = dragPreview?.source === wrappedFields ? dragPreview.items : wrappedFields;
  const [hoveredIndex, setHoveredIndex] = useState<HoveredFieldItemIndex>("draggingIsOff");
  const listRef = useRef<HTMLUListElement | null>(null);
  const [draggingItemMovedFromInitialPosition, setDraggingItemMovedFromInitialPosition] = useState(false);

  const onEndReordering = useCallback(
    (draggingItem: FieldWrapper<T>) => {
      const toIndex = getInsertToIndex(Array.from(listRef.current?.children || []));

      const values = [...areaFields];
      values.splice(draggingItem.index, 1);
      values.splice(toIndex, 0, draggingItem);
      setDragPreview({ source: wrappedFields, items: values });
      setHoveredIndex("draggingIsOff");
      setDraggingItemMovedFromInitialPosition(false);

      setTimeout(() => {
        onMoveItem(draggingItem.field, toIndex);
      }, 0);
    },
    [areaFields, wrappedFields, onMoveItem]
  );

  const isNewFieldToAdd = useCallback(
    (item: FieldWrapper<T> | TMeta) => {
      const itemAreaFieldType = extractAreaItemType(item);
      if (itemAreaFieldType !== areaFieldType) {
        return true;
      }
      if (isFieldWrapper(item)) {
        const isSelf = areaFields.find((v) => getKeyValue(v) == getKeyValue(item) && v.type === item.type);
        return !isSelf;
      }
      return false;
    },
    [areaFieldType, areaFields, getKeyValue]
  );

  const collect = useCallback((monitor: DropTargetMonitor) => {
    return {
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      didDrop: monitor.didDrop(),
      draggingItem: monitor.getItem<{ type: string; index: number }>(),
    };
  }, []);

  const [{ canDrop, isOver, didDrop, draggingItem }, drop] = useDrop({
    accept: [...acceptedDropTypes, areaFieldType],
    collect,
    drop: (item: FieldWrapper<T> | TMeta) => {
      const meta = extractMeta(item);
      if (isNewFieldToAdd(item) && meta) {
        const idx = getInsertToIndex(Array.from(listRef.current?.children || []));
        const newFieldItem = createListItem(meta as TMeta);
        if (!newFieldItem) {
          return;
        }
        const values = [...areaFields];
        const newValues = insertItemAt(values, wrapItem(newFieldItem, idx, areaFieldType), idx);
        setDragPreview({ source: wrappedFields, items: newValues });
        setHoveredIndex("draggingIsOff");
        setDraggingItemMovedFromInitialPosition(false);

        setTimeout(() => {
          onAddItem(newFieldItem, idx);
        }, 0);
      }
      return { dropContainerType: areaFieldType };
    },
    canDrop: (item: FieldWrapper<T> | TMeta, monitor: DropTargetMonitor) => {
      if (readonly || !monitor.isOver()) {
        return false;
      }
      if (isNewFieldToAdd(item)) {
        if (canDropItem) {
          return canDropItem(item);
        }
      }
      return true;
    },
  });

  useDragLeave(() => setHoveredIndex("draggingIsOff"), isOver, didDrop);

  const handleItemHovered = useCallback(
    (hoveredItem: FieldWrapper<T>) => {
      if (!canDrop) {
        return;
      }
      if (hoveredIndex !== "draggingIsOff" && hoveredIndex !== hoveredItem.index) {
        setDraggingItemMovedFromInitialPosition(true);
      }
      setHoveredIndex(hoveredItem.index);
    },
    [canDrop, hoveredIndex]
  );

  useEffect(() => {
    if (!draggingItem) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setDraggingItemMovedFromInitialPosition(false);
    }
  }, [draggingItem]);

  const isPositioningField = !!draggingItem && hoveredIndex !== "draggingIsOff";

  const handleAddField = () => {
    setShowAddFieldMenu(true);
  };

  const handleCalculationItemClick = (calculationType: CalculationType) => {
    setShowAddFieldMenu(false);
    setSelectedCalculationType(calculationType);
  };

  const handleApplyCustomCalculation = (data: CustomColumnData) => {
    const field = newCustomColumnField(data);
    const newFieldItem = createListItem(field.meta as TMeta);
    if (!newFieldItem) {
      return;
    }
    onAddItem(newFieldItem, fields.length);
    setSelectedCalculationType(null);
  };

  const handleContainerMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const isFreeSpace = event.target === event.currentTarget || event.target === listRef.current;
    setFreeSpaceHovered(isFreeSpace);
    if (isFreeSpace) {
      if (!freeSpaceHighlighted && hoverTimeoutRef.current === undefined) {
        hoverTimeoutRef.current = setTimeout(() => {
          hoverTimeoutRef.current = undefined;
          setFreeSpaceHighlighted(true);
        }, HOVER_DELAY_MS);
      }
      return;
    }

    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = undefined;
    setFreeSpaceHighlighted(false);
  };

  const handleContainerMouseLeave = () => {
    setFreeSpaceHovered(false);
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = undefined;
    setFreeSpaceHighlighted(false);
  };

  const activeDestination = canSelectDestination && destination?.id === destinationId;
  const activateDestination =
    canSelectDestination && destinationStore ? () => destinationStore.select(destinationId) : undefined;

  return (
    <>
      <FieldContainerHeading
        title={title}
        actions={headingActions}
        addingActive={activeDestination}
        onToggleAdding={activeDestination ? () => destinationStore?.select() : activateDestination}
      />
      <Tooltip
        title="Click to add fields here"
        open={!!activateDestination && freeSpaceHighlighted && !activeDestination && !draggingItem}
        placement="top-end"
        disableInteractive
        arrow
      >
        <Box
          onMouseMove={handleContainerMouseMove}
          onMouseLeave={handleContainerMouseLeave}
          onClick={(event) => {
            if (event.target === event.currentTarget || event.target === listRef.current) {
              activateDestination?.();
            }
          }}
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.grey,
            minHeight: readonly ? "40px" : "90px",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
            p: ".5rem",
            borderColor: canDrop && isOver ? theme.palette.primary.main : theme.palette.divider,
            ...(containerSx as AnyObject),
            cursor: activateDestination && freeSpaceHovered && !draggingItem ? "pointer" : undefined,
            ...(activateDestination &&
              (activeDestination || (freeSpaceHighlighted && !draggingItem)) && {
                borderColor: theme.palette.primary.main,
                borderStyle: activeDestination ? "solid" : "dashed",
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              }),
          })}
          ref={(node) => {
            if (node) {
              drop(node as HTMLElement);
            }
          }}
          role={"Dustbin"}
        >
          {activeDestination && <FieldDestinationHeader title={title} onClose={() => destinationStore?.select()} />}
          <List ref={listRef} sx={{ p: 0, display: "flex", flexDirection: "column", gap: "5px" }}>
            {areaFields.map((item, index) => {
              const addShifting =
                isPositioningField && hoveredIndexIsDefined(hoveredIndex) && item.index >= hoveredIndex;
              const hideDraggingItem =
                draggingItem &&
                item.type === draggingItem.type &&
                item.index === draggingItem.index &&
                isPositioningField;
              return (
                <AreaFieldItemContainer
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${index}-field-item${getKeyValue(item)}`}
                  canBeRemoved={!readonly}
                  areaField={item}
                  createItem={renderListItem}
                  formatCaption={formatCaption}
                  onRemoveItem={onRemoveItem}
                  onEndReordering={onEndReordering}
                  showOptions={showOptions}
                  acceptedDropTypes={acceptedDropTypes}
                  canDrop={!!canDrop}
                  onItemHovered={handleItemHovered}
                  draggingItemMovedFromInitialPosition={draggingItemMovedFromInitialPosition}
                  addShifting={addShifting}
                  hideDraggingItem={hideDraggingItem}
                  readonly={readonly ?? false}
                />
              );
            })}
            {isOver && !!canDrop && (
              <DropFieldPlaceholder hide={!isPositioningField && draggingItem && draggingItem.type === areaFieldType} />
            )}
            {!readonly && !hideAddField && (
              <AddField
                onDragEnter={() => setHoveredIndex("nonSortableItemHovered")}
                onAddField={addFieldAction ? (event) => addFieldAction.onClick(event.currentTarget) : handleAddField}
                ref={setAddFieldMenuItemAnchorEl}
                disabled={!addFieldAction && !canAddCustomField}
              >
                {addFieldAction?.label}
              </AddField>
            )}
            {showAddFieldMenu && (
              <AddFieldMenu
                anchorEl={addFieldMenuItemAnchorEl}
                onCalculationItemClick={handleCalculationItemClick}
                onClose={() => setShowAddFieldMenu(false)}
              />
            )}
            {selectedCalculationType && (
              <>
                {selectedCalculationType === CalculationType.ConditionValue && (
                  <FieldOptionPopupContainer
                    anchorEl={addFieldMenuItemAnchorEl}
                    cancel={() => setSelectedCalculationType(null)}
                  >
                    <CustomColumnConditionValueSetup
                      fieldGuid={undefined}
                      onClose={() => setSelectedCalculationType(null)}
                      onApply={handleApplyCustomCalculation}
                      readonly={readonly ?? false}
                    />
                  </FieldOptionPopupContainer>
                )}
                {selectedCalculationType === CalculationType.PercentageOfTotal && (
                  <FieldOptionPopupContainer
                    anchorEl={addFieldMenuItemAnchorEl}
                    cancel={() => setSelectedCalculationType(null)}
                  >
                    <NewCustomColumnCalculationSetup
                      fieldGuid={undefined}
                      calculationType={selectedCalculationType}
                      onClose={() => setSelectedCalculationType(null)}
                      onApply={handleApplyCustomCalculation}
                      readonly={readonly ?? false}
                    />
                  </FieldOptionPopupContainer>
                )}
                {selectedCalculationType === CalculationType.CustomFormula && (
                  <CustomFormulaDialog
                    open
                    onClose={() => setSelectedCalculationType(null)}
                    onApply={handleApplyCustomCalculation}
                  />
                )}
              </>
            )}
          </List>
        </Box>
      </Tooltip>
    </>
  );
};

export default DropFieldContainer;
